import React, { useEffect, useState } from 'react';
import { collection, getDocs, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db } from '../firebaseConfig';
import { getAuth } from 'firebase/auth';
import moment from "moment";
import { useNavigate } from "react-router-dom";
import './AdminPortal.css';
import { addJobTotal } from "../utils/addJobTotal";
import { recalculateJobLabor } from "../utils/recalculateJobLabor";


const AdminPortal = () => {
  const [allJobs, setAllJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedJob, setEditedJob] = useState({});
  const [selectedActions, setSelectedActions] = useState({});
  const [showNotePopup, setShowNotePopup] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]); // Update to handle multiple files
  const [uploading, setUploading] = useState(false);
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const auth = getAuth();
  const [userRole, setUserRole] = useState(null);
  const navigate = useNavigate();
  const isStaff = ["Admin", "Manager", "Assistant"].includes(userRole);

  const [jobTotals, setJobTotals] = useState({});
  const [showTotalPopup, setShowTotalPopup] = useState(false);
const [totalInput, setTotalInput] = useState("");


  const [jobPage, setJobPage] = useState(1);
  const [userPage, setUserPage] = useState(1);
  const itemsPerPage = 25;

  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });

  const sortData = (data, key, direction) => {
    return [...data].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'ascending' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'ascending' ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const SortButton = ({ columnKey, sortConfig, onSort }) => (
    <button onClick={() => onSort(columnKey)}>
      {sortConfig.key === columnKey ? (sortConfig.direction === 'ascending' ? '↑' : '↓') : '↕'}
    </button>
  );

  const [siteNames, setSiteNames] = useState([]); // State to store all site names

  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [selectedTimeOffRequest, setSelectedTimeOffRequest] = useState(null);
  const [timeOffPage, setTimeOffPage] = useState(1);
  const [isTimeOffFullPage, setIsTimeOffFullPage] = useState(false);

  const serviceTypes = [
    "Proposal", "Meeting", "Construction", "Maintenance", "Action Items", 
    "Cleaning", "Bulk Trash", "Inspection", "Electrical", "Plumbing", 
    "HVAC", "Concrete"
  ];

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const jobsRef = collection(db, 'jobs');
        const querySnapshot = await getDocs(jobsRef);
        const jobs = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        const visibleJobs = jobs.filter(
          job =>
            job.status === "Active" // 👈 ONLY Active
        );
        
        setAllJobs(visibleJobs);
        
      } catch (error) {
        console.error('Error fetching jobs:', error);
      }
    };

    const fetchUsers = async () => {
      try {
        const usersRef = collection(db, 'users');
        const querySnapshot = await getDocs(usersRef);
        const userList = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setUsers(userList);

        const currentUser = auth.currentUser;
        if (currentUser) {
          const userDoc = querySnapshot.docs.find((doc) => doc.id === currentUser.uid);
          if (userDoc) {
            setUserRole(userDoc.data().role);
          }
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    const fetchJobTotals = async () => {
      try {
        const snap = await getDocs(collection(db, "job_totals"));
        const totalsMap = {};
        snap.forEach((doc) => {
          const data = doc.data();
          if (data?.jobId) {
            totalsMap[String(data.jobId)] = data.total;
          }
        });
        setJobTotals(totalsMap);
      } catch (err) {
        console.error("Error fetching job totals:", err);
      }
    };
    

    const fetchSiteNames = async () => {
      try {
        const jobsRef = collection(db, 'jobs');
        const querySnapshot = await getDocs(jobsRef);
        const sites = querySnapshot.docs.map((doc) => ({
          siteName: doc.data().siteName,
          address: doc.data().address,
          city: doc.data().city,
          state: doc.data().state,
        }));
        setSiteNames(sites);
      } catch (error) {
        console.error('Error fetching site names:', error);
      }
    };

    const fetchTimeOffRequests = async () => {
      try {
        const timeOffRef = collection(db, 'timeOff');
        const querySnapshot = await getDocs(timeOffRef);
        const requests = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTimeOffRequests(requests);
      } catch (error) {
        console.error('Error fetching time off requests:', error);
      }
    };

    fetchJobTotals();
    fetchJobs();
    fetchUsers();
    fetchSiteNames();
    fetchTimeOffRequests();
  }, [auth]);

  const toggleTimeOffFullPage = () => {
    setIsTimeOffFullPage((prev) => {
      if (!prev) {
        setIsJobsFullPage(false);
      }
      return !prev;
    });
  };

  const handleApproveRequest = async (id) => {
    try {
      const requestRef = doc(db, 'timeOff', id);
      await updateDoc(requestRef, { status: 'Approved' });
      setTimeOffRequests((prevRequests) =>
        prevRequests.map((req) => (req.id === id ? { ...req, status: 'Approved' } : req))
      );
      setSelectedTimeOffRequest((prev) => ({ ...prev, status: 'Approved' }));
    } catch (error) {
      console.error('Error approving request:', error);
    }
  };

  const handleDenyRequest = async (id) => {
    try {
      const requestRef = doc(db, 'timeOff', id);
      await updateDoc(requestRef, { status: 'Denied' });
      setTimeOffRequests((prevRequests) =>
        prevRequests.map((req) => (req.id === id ? { ...req, status: 'Denied' } : req))
      );
      setSelectedTimeOffRequest((prev) => ({ ...prev, status: 'Denied' }));
    } catch (error) {
      console.error('Error denying request:', error);
    }
  };

  const handleDeleteRequest = async (id) => {
    try {
      const requestRef = doc(db, 'timeOff', id);
      await deleteDoc(requestRef);
      setTimeOffRequests((prevRequests) => prevRequests.filter((req) => req.id !== id));
      setSelectedTimeOffRequest(null);
    } catch (error) {
      console.error('Error deleting request:', error);
    }
  };

  const handleSaveTimeOffNote = async () => {
    if (selectedTimeOffRequest && (noteText.trim() !== '' || selectedFiles.length > 0)) {
      setUploading(true);
      let uploadedFiles = [];

      if (selectedFiles.length > 0) {
        const storage = getStorage();
        const uploadPromises = selectedFiles.map(async (file) => {
          const storageRef = ref(storage, `timeOff/${selectedTimeOffRequest.id}/${file.name}`);
          await uploadBytes(storageRef, file);
          return await getDownloadURL(storageRef);
        });
        uploadedFiles = await Promise.all(uploadPromises);
      }

      const newNote = {
        text: noteText,
        timestamp: new Date().toLocaleString(),
        files: uploadedFiles,
      };

      const updatedNotes = Array.isArray(selectedTimeOffRequest.notes)
        ? [...selectedTimeOffRequest.notes, newNote]
        : [newNote];
      const requestRef = doc(db, 'timeOff', selectedTimeOffRequest.id);
      await updateDoc(requestRef, { notes: updatedNotes });

      setSelectedTimeOffRequest((prev) => ({ ...prev, notes: updatedNotes }));
      setTimeOffRequests((prevRequests) =>
        prevRequests.map((req) =>
          req.id === selectedTimeOffRequest.id ? { ...req, notes: updatedNotes } : req
        )
      );

      setNoteText('');
      setSelectedFiles([]);
      setUploading(false);
      setShowNotePopup(false);
    }
  };

  const closeModal = () => {
    setSelectedJob(null);
    setIsEditing(false);
  };

  const closeNotePopup = () => setShowNotePopup(false);
  const closeVerificationPopup = () => setShowVerificationPopup(false);

  const markJobCompleted = async () => {
    if (selectedJob) {
      try {
        const jobDoc = doc(db, "jobs", selectedJob.id);
        const updatedStatus = getUpdatedStatus(selectedJob.serviceType);
  
        // Update Firestore
        await updateDoc(jobDoc, { status: updatedStatus });
  
        // Update state instantly (UI)
        setAllJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.id === selectedJob.id ? { ...job, status: updatedStatus } : job
          )
        );
  
        // Update filtered list too (so it reacts immediately)
        setFilteredJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.id === selectedJob.id ? { ...job, status: updatedStatus } : job
          )
        );
  
        // Update the selected job modal
        setSelectedJob((prev) => ({ ...prev, status: updatedStatus }));
  
        closeVerificationPopup();
      } catch (error) {
        console.error("Error marking job as completed:", error);
      }
    }
  };
  

  const updateUserRole = async (userId, newRole) => {
    try {
      const userDoc = doc(db, 'users', userId);
      await updateDoc(userDoc, { role: newRole });
      setUsers(users.map(user => (user.id === userId ? { ...user, role: newRole } : user)));
    } catch (error) {
      console.error('Error updating user role:', error);
    }
  };

  const deleteUser = async (userId) => {
    try {
      const userDoc = doc(db, 'users', userId);
      await deleteDoc(userDoc);
      setUsers(users.filter(user => user.id !== userId));
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const handleActionChange = (userId, action) => {
    setSelectedActions((prev) => ({ ...prev, [userId]: action }));
  };

  const handleConfirmAction = (userId) => {
    const action = selectedActions[userId];
    if (action === 'delete') {
      deleteUser(userId);
    } else if (action) {
      updateUserRole(userId, action);
    }
    setSelectedActions((prev) => ({ ...prev, [userId]: '' }));
  };

  const handleEditClick = () => {
    setIsEditing(true);
  
    setEditedJob({
      ...selectedJob,
  
      // ✅ Normalize techAssign
      techAssign: Array.isArray(selectedJob.techAssign)
        ? selectedJob.techAssign
        : [selectedJob.techAssign || ""],
  
      // ✅ Derive startTimeMode for time window editor
      startTimeMode: selectedJob.startTime?.includes(":")
        ? "custom"
        : "preset",
  
      // ✅ Ensure recurrence object always exists
      recurrence: selectedJob.recurrence || { type: "none" },
    });
  };
  

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedJob((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files)); // Update to handle multiple files
  };

  const handleSaveChanges = async () => {
    try {
      const jobDoc = doc(db, 'jobs', editedJob.id);
      await updateDoc(jobDoc, editedJob);
      setAllJobs((prevJobs) =>
        prevJobs.map((job) => (job.id === editedJob.id ? { ...editedJob } : job))
      );
      setIsEditing(false);
      setSelectedJob({ ...editedJob });
    } catch (error) {
      console.error('Error saving job changes:', error);
    }
  };

  const uploadFiles = async (files, jobId) => {
    const storage = getStorage();
    const uploadPromises = files.map(async (file) => {
      const storageRef = ref(storage, `notes/${jobId}/${file.name}`);
      await uploadBytes(storageRef, file);
      const fileURL = await getDownloadURL(storageRef);
      return { fileURL, fileName: file.name };
    });
    return await Promise.all(uploadPromises);
  };

  const saveNote = async () => {
    if (selectedJob && (noteText.trim() !== "" || selectedFiles.length > 0)) {
      setUploading(true); // Start uploading state
      let fileURLs = [];

      try {
        if (selectedFiles.length > 0) {
          const storage = getStorage();
          const uploadPromises = selectedFiles.map(async (file) => {
            const storageRef = ref(storage, `notes/${selectedJob.id}/${file.name}`);
            await uploadBytes(storageRef, file);
            return await getDownloadURL(storageRef);
          });
          fileURLs = await Promise.all(uploadPromises);
        }

        const currentUser = auth.currentUser;
        const userFullName = currentUser ? `${currentUser.displayName || 'Unknown User'}` : 'Unknown User';

        const newNote = {
          text: noteText,
          timestamp: new Date().toISOString(),
          author: userFullName,
          fileURLs,
          fileNames: selectedFiles.map(file => file.name),
        };

        const updatedNotes = Array.isArray(selectedJob.notes) ? [...selectedJob.notes, newNote] : [newNote];
        const jobDoc = doc(db, "jobs", selectedJob.id);
        await updateDoc(jobDoc, { notes: updatedNotes });

        // Update the state to reflect the new note
        setSelectedJob((prev) => ({ ...prev, notes: updatedNotes }));
        setAllJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.id === selectedJob.id ? { ...job, notes: updatedNotes } : job
          )
        );

        setNoteText(""); // Clear note text
        setSelectedFiles([]); // Clear selected files
      } catch (error) {
        console.error("Error saving note:", error);
      } finally {
        setUploading(false); // End uploading state
        closeNotePopup(); // Close the popup
      }
    }
  };

  const getBackgroundColor = (job) => {
    if (!job.startDate) return 'white';

    const startDate = moment(job.startDate);
    const endDate = job.endDate ? moment(job.endDate) : null;
    const now = moment();
    const diffDaysStart = now.diff(startDate, 'days');
    const diffDaysEnd = endDate ? now.diff(endDate, 'days') : null;

    if (diffDaysEnd > 1 || (!endDate && diffDaysStart > 1)) {
      return 'red';
    }

    switch (job.serviceType) {
      case 'Proposal':
        return '#FFC0CB'; // Warm pinkish color
      case 'Meeting':
        return '#98FB98'; // Warm greenish color
      case 'Action Items':
        return '#ADD8E6'; // Warm soft bluish color
      case 'Inspection':
        return '#FFD700'; // Warm soft yellowish goldish color
      default:
        return 'white';
    }
  };

  const getButtonTitle = (serviceType) => {
    switch (serviceType) {
      case 'Proposal':
        return 'Proposal Sent';
      case 'Meeting':
        return 'Meeting Completed';
      case 'Action Items':
        return 'Action Item Completed';
      case 'Inspection':
        return 'Inspection Completed';
      default:
        return 'Mark Job as Completed';
    }
  };

  const getVerificationMessage = (serviceType) => {
    switch (serviceType) {
      case 'Proposal':
        return 'Are you sure the proposal was sent?';
      case 'Meeting':
        return 'Are you sure the meeting was completed?';
      case 'Action Items':
        return 'Are you sure the action item was completed?';
      case 'Inspection':
        return 'Are you sure the inspection was completed?';
      default:
        return 'Are you sure you want to complete this job?';
    }
  };

  const getUpdatedStatus = (serviceType) => {
    switch (serviceType) {
      case 'Proposal':
        return 'Proposal Sent';
      case 'Meeting':
        return 'Meeting Completed';
      case 'Action Items':
        return 'Action Item Completed';
      case 'Inspection':
        return 'Inspection Completed';
      default:
        return 'Completed';
    }
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredJobs, setFilteredJobs] = useState([]);

  useEffect(() => {
    filterAndSortJobs();
  }, [allJobs, searchTerm]);
  
  const [showDepositPrice2Popup, setShowDepositPrice2Popup] = useState(false);
  const [depositPrice2, setDepositPrice2] = useState('');
  const [showChangePricePopup, setShowChangePricePopup] = useState(false);
  const [changePrice, setChangePrice] = useState('');
  const [showChangePrice2Popup, setShowChangePrice2Popup] = useState(false);
  const [changePrice2, setChangePrice2] = useState('');

  const handleSaveDepositPrice2 = async () => {
    if (selectedJob && depositPrice2) {
      const jobDoc = doc(db, 'jobs', selectedJob.id);
      await updateDoc(jobDoc, { depositPrice2: parseFloat(depositPrice2), serviceType: 'Additional Deposit' });
      setAllJobs((prevJobs) =>
        prevJobs.map((job) => (job.id === selectedJob.id ? { ...job, depositPrice2: parseFloat(depositPrice2), serviceType: 'Additional Deposit' } : job))
      );
      setShowDepositPrice2Popup(false);
      setDepositPrice2('');
    }
  };

  const handleSaveChangePrice = async () => {
    if (selectedJob && changePrice) {
      const jobDoc = doc(db, 'jobs', selectedJob.id);
      await updateDoc(jobDoc, { changePrice: parseFloat(changePrice) });
      setAllJobs((prevJobs) =>
        prevJobs.map((job) => (job.id === selectedJob.id ? { ...job, changePrice: parseFloat(changePrice) } : job))
      );
      setShowChangePricePopup(false);
      setChangePrice('');
    }
  };

  const handleSaveChangePrice2 = async () => {
    if (selectedJob && changePrice2) {
      const jobDoc = doc(db, 'jobs', selectedJob.id);
      await updateDoc(jobDoc, { changePrice2: parseFloat(changePrice2) });
      setAllJobs((prevJobs) =>
        prevJobs.map((job) => (job.id === selectedJob.id ? { ...job, changePrice2: parseFloat(changePrice2) } : job))
      );
      setShowChangePrice2Popup(false);
      setChangePrice2('');
    }
  };

  const calculateRemainingBalance = (job) => {
    let remainingBalance = job.proposalPrice || 0;
    if (job.depositPrice) remainingBalance -= job.depositPrice;
    if (job.depositPrice2) remainingBalance -= job.depositPrice2;
    if (job.changePrice) remainingBalance += job.changePrice;
    if (job.changePrice2) remainingBalance += job.changePrice2;
    return remainingBalance;
  };

  const paginatedJobs = allJobs.slice((jobPage - 1) * itemsPerPage, jobPage * itemsPerPage);
  const paginatedUsers = users.slice((userPage - 1) * itemsPerPage, userPage * itemsPerPage);
  const paginatedTimeOffRequests = timeOffRequests.slice(
    (timeOffPage - 1) * itemsPerPage,
    timeOffPage * itemsPerPage
  );

  const jobTotalPages = Math.ceil(allJobs.length / itemsPerPage);
  const userTotalPages = Math.ceil(users.length / itemsPerPage);
  const timeOffTotalPages = Math.ceil(timeOffRequests.length / itemsPerPage);

  const filterAndSortJobs = () => {
    const lowerSearch = searchTerm.toLowerCase();
  
    const filtered = allJobs.filter((job) => {
      const siteName = job.siteName?.toLowerCase() || '';
      const city = job.city?.toLowerCase() || '';
      const scope = job.scope?.toLowerCase() || '';
      const wo = job.wo?.toString().toLowerCase() || '';
      const clientWO = job.clientWorkOrder?.toLowerCase() || '';
  
      return (
        siteName.includes(lowerSearch) ||
        city.includes(lowerSearch) ||
        scope.includes(lowerSearch) ||
        wo.includes(lowerSearch) ||
        clientWO.includes(lowerSearch)
      );
    });
  
    setFilteredJobs(filtered);
  };
  
  const [isJobsFullPage, setIsJobsFullPage] = useState(false);


  const toggleJobsFullPage = () => {
    setIsJobsFullPage((prev) => !prev);
  };

  const handleSiteNameChange = (e) => {
    const selectedSite = siteNames.find((site) => site.siteName === e.target.value);
    if (selectedSite) {
      setEditedJob((prev) => ({
        ...prev,
        siteName: selectedSite.siteName,
        address: selectedSite.address,
        city: selectedSite.city,
        state: selectedSite.state,
      }));
    }
  };

  return (
    <div>
      {/* ✅ STAFF-ONLY TOP NAV (Admin / Manager / Assistant) */}
{isStaff && (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      gap: 10,
      marginBottom: 14,
      flexWrap: "wrap",
    }}
  >
    <button
      onClick={() => navigate("/AdminPortal")}
      style={{
        padding: "10px 14px",
        borderRadius: 10,
        border: "1px solid #1d4ed8",
        background: "#1d4ed8",
        color: "#fff",
        fontWeight: 800,
        cursor: "pointer",
      }}
    >
      Admin Portal
    </button>

    <button
      onClick={() => navigate("/Contractors")}
      style={{
        padding: "10px 14px",
        borderRadius: 10,
        border: "1px solid #1d4ed8",
        background: "#93c5fd",
        color: "#0f172a",
        fontWeight: 800,
        cursor: "pointer",
      }}
    >
      Team Dashboard
    </button>

    <button
      onClick={() => navigate("/")}
      style={{
        padding: "10px 14px",
        borderRadius: 10,
        border: "1px solid #1d4ed8",
        background: "#e0e7ff",
        color: "#0f172a",
        fontWeight: 800,
        cursor: "pointer",
      }}
    >
      Home
    </button>
  </div>
)}

      <h1>Admin Portal
      <p>Manage jobs, users, and system settings from your dashboard.</p></h1>

      <div className="search-sort-container">
  <input
    type="text"
    placeholder="Search by Site Name, City, Scope, WO#, or Client WO#"
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
  />
</div>

        <div
          className={`dashboard-section ${isJobsFullPage ? 'full-page' : ''}`}
        >
          <h2 onClick={toggleJobsFullPage} style={{ cursor: 'pointer' }}>
            All Jobs {isJobsFullPage ? '(Click to Minimize)' : '(Click to Expand)'}
          </h2>
          {filteredJobs.length > 0 ? (
  <>
    <div className="table-container">
      <div className="scrollable-container">
        <table>
          <thead>
            <tr>
              <th>
                WO# <SortButton columnKey="wo" sortConfig={sortConfig} onSort={handleSort} />
              </th>
              <th>
                Site Name <SortButton columnKey="siteName" sortConfig={sortConfig} onSort={handleSort} />
              </th>
              <th>
                City <SortButton columnKey="city" sortConfig={sortConfig} onSort={handleSort} />
              </th>
              <th>
                Start Date <SortButton columnKey="startDate" sortConfig={sortConfig} onSort={handleSort} />
              </th>
              <th>
                Service Type <SortButton columnKey="serviceType" sortConfig={sortConfig} onSort={handleSort} />
              </th>
              <th>Scope</th>
              <th>
                Tech <SortButton columnKey="techAssign" sortConfig={sortConfig} onSort={handleSort} />
              </th>
              <th>Total</th>

              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {sortData(filteredJobs.slice((jobPage - 1) * itemsPerPage, jobPage * itemsPerPage), sortConfig.key, sortConfig.direction).map((job) => (
              <tr key={job.id} style={{ backgroundColor: getBackgroundColor(job) }}>
                <td>{job.wo}</td>
                <td>{job.siteName}</td>
                <td>{job.city}</td>
                <td>{job.startDate}</td>
                <td>{job.serviceType}</td>
                <td>{job.scope}</td>
                <td>{job.techAssign}</td>
                <td>
  {jobTotals[String(job.wo)] != null
    ? `$${jobTotals[String(job.wo)].toLocaleString()}`
    : "—"}
</td>

                <td>
                  <strong>Created On:</strong>{' '}
                  {job.creationDate ? new Date(job.creationDate.seconds * 1000).toLocaleDateString() : 'N/A'}
                  <br />
                  <button
  className="azul-button"
  onClick={async () => {
    setSelectedJob(job);
    await recalculateJobLabor(job.wo);
  }}
>

                    Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    <div className="pagination">
      <button
        className="smallazul-button"
        onClick={() => setJobPage(jobPage - 1)}
        disabled={jobPage === 1}
      >
        Previous
      </button>
      <span>
        Page {jobPage} of {jobTotalPages}
      </span>
      <button
        className="smallazul-button"
        onClick={() => setJobPage(jobPage + 1)}
        disabled={jobPage === jobTotalPages}
      >
        Next
      </button>
    </div>
  </>
) : (
  <p>No jobs available.</p>
)}

        </div>

      {selectedJob && (
        <>
          <div className="modal-overlay" onClick={closeModal}></div>
          <div className="modal">
            <h2>Job Details</h2>
            <button
              className="yellow-button" onClick={() => navigate(`/JobChecklist/${selectedJob.id}`)}
            >
              📝 Checklist
            </button>
            <button
  className="red-button"
  onClick={() => navigate(`/JobFinancials/${selectedJob.id}`)}
>
  💵 Financials
</button>

            {isEditing ? (
              <>
                <label>
                  Site Name:
                  <select
                    name="siteName"
                    value={editedJob.siteName || ''}
                    onChange={handleSiteNameChange}
                  >
                    <option value="" disabled>Select a site</option>
                    {siteNames.map((site, index) => (
                      <option key={index} value={site.siteName}>
                        {site.siteName}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Address:
                  <input
                    type="text"
                    name="address"
                    value={editedJob.address || ''}
                    onChange={handleInputChange}
                    readOnly
                  />
                </label>
                <label>
                  City:
                  <input
                    type="text"
                    name="city"
                    value={editedJob.city || ''}
                    onChange={handleInputChange}
                    readOnly
                  />
                </label>
                <label>
                  State:
                  <input
                    type="text"
                    name="state"
                    value={editedJob.state || ''}
                    onChange={handleInputChange}
                    readOnly
                  />
                </label>
                <label>
                  Work Order:
                  <input
  type="text"
  name="wo"
  value={editedJob.wo || ''}
  readOnly
/>
                </label>
                <label>
                  Start Date:
                  <input
                    type="text"
                    name="startDate"
                    value={editedJob.startDate || ''}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Start Time:
                  <input
                    type="text"
                    name="startTime"
                    value={editedJob.startTime || ''}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  End Date:
                  <input
                    type="text"
                    name="endDate"
                    value={editedJob.endDate || ''}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  End Time:
                  <input
                    type="text"
                    name="endTime"
                    value={editedJob.endTime || ''}
                    onChange={handleInputChange}
                  />
                </label>
                <label>Recurrence:</label>
<select
  value={editedJob.recurrence?.type || "none"}
  onChange={(e) =>
    setEditedJob(prev => ({
      ...prev,
      recurrence: {
        ...prev.recurrence,
        type: e.target.value,
      },
    }))
  }
>
  <option value="none">One-Time</option>
  <option value="daily">Daily</option>
  <option value="weekly">Weekly</option>
  <option value="biweekly">Bi-Weekly</option>
  <option value="monthly">Monthly</option>
  <option value="bimonthly">Bi-Monthly</option>
  <option value="quarterly">Quarterly</option>
  <option value="biyearly">Bi-Yearly</option>
  <option value="yearly">Yearly</option>
  <option value="customDates">Custom Dates</option>
</select>

{editedJob.recurrence?.type === "weekly" && (
  <>
    <label>Days of Week:</label>
    {[0,1,2,3,4,5,6].map(d => (
      <label key={d} style={{ marginRight: 8 }}>
        <input
          type="checkbox"
          checked={editedJob.recurrence?.daysOfWeek?.includes(d)}
          onChange={(e) => {
            const days = editedJob.recurrence.daysOfWeek || [];
            const updated = e.target.checked
              ? [...days, d]
              : days.filter(x => x !== d);

            setEditedJob(prev => ({
              ...prev,
              recurrence: {
                ...prev.recurrence,
                daysOfWeek: updated,
              },
            }));
          }}
        />
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d]}
      </label>
    ))}
  </>
)}

{editedJob.recurrence?.type === "customDates" && (
  <>
    <label>Custom Dates (comma separated):</label>
    <input
      type="text"
      value={(editedJob.recurrence.customDates || []).join(",")}
      onChange={(e) =>
        setEditedJob(prev => ({
          ...prev,
          recurrence: {
            ...prev.recurrence,
            customDates: e.target.value
              .split(",")
              .map(d => d.trim())
              .filter(Boolean),
          },
        }))
      }
    />
  </>
)}


                <label>
                  Service Type:
                  <select
                    name="serviceType"
                    value={editedJob.serviceType || ''}
                    onChange={(e) => setEditedJob((prev) => ({ ...prev, serviceType: e.target.value }))}
                  >
                    <option value="" disabled>Select Service Type</option>
                    {serviceTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Scope:
                  <input
                    type="text"
                    name="scope"
                    value={editedJob.scope || ''}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Job Description:
                  <input
                    type="text"
                    name="jobDescription"
                    value={editedJob.jobDescription || ''}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
  Client Phone:
  <input
    type="text"
    name="clientPhone"
    value={editedJob.clientPhone || ''}
    onChange={handleInputChange}
  />
</label>

<label>
  Client Email:
  <input
    type="email"
    name="clientEmail"
    value={editedJob.clientEmail || ''}
    onChange={handleInputChange}
  />
</label>

                <label>
                  Tech Assign:
                  <input
                    type="text"
                    name="techAssign"
                    value={editedJob.techAssign || ''}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Status:
                  <input
                    type="text"
                    name="status"
                    value={editedJob.status || ''}
                    onChange={handleInputChange}
                  />
                </label>
                <label>
                  Notes:
                  <input
                    type="text"
                    name="notes"
                    value={editedJob.notes || ''}
                    onChange={handleInputChange}
                  />
                </label>
                <div className="modal-buttons">
                  <button className='green-button' onClick={handleSaveChanges}>Save</button>
                  <button className='red-button' onClick={closeModal}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <p><strong>Site Name:</strong> {selectedJob.siteName}</p>
                <p><strong>Work Order:</strong> {selectedJob.wo}</p>
                <p><strong>Address:</strong> {selectedJob.address}</p>
                <p><strong>City:</strong> {selectedJob.city}</p>
                <p><strong>State:</strong> {selectedJob.state}</p>
                <p><strong>Start Date:</strong> {selectedJob.startDate}</p>
                <p>
  <strong>Time Window:</strong>{" "}
  {selectedJob.startTimeMode === "custom"
    ? `Custom (${selectedJob.startTime})`
    : selectedJob.startTime || "ANY"}
</p>

                <p><strong>End Date:</strong> {selectedJob.endDate}</p>
                <p><strong>End Time:</strong> {selectedJob.endTime}</p>
                <p>
  <strong>Recurrence:</strong>{" "}
  {selectedJob.recurrence?.type
    ? selectedJob.recurrence.type
    : "One-Time"}
</p>

{selectedJob.recurrence?.type === "weekly" && (
  <p>
    <strong>Days:</strong>{" "}
    {(selectedJob.recurrence.daysOfWeek || [])
      .map(d => ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d])
      .join(", ")}
  </p>
)}

{selectedJob.recurrence?.type === "customDates" && (
  <p>
    <strong>Custom Dates:</strong>{" "}
    {(selectedJob.recurrence.customDates || []).join(", ")}
  </p>
)}

                <p><strong>Service Type:</strong> {selectedJob.serviceType}</p>
                <p><strong>Scope:</strong> {selectedJob.scope}</p>
                <p><strong>Job Description:</strong> {selectedJob.jobDescription}</p>
                <p><strong>Client Phone:</strong> {selectedJob.clientPhone || "N/A"}</p>
<p><strong>Client Email:</strong> {selectedJob.clientEmail || "N/A"}</p>

                <p><strong>Tech Assign:</strong> {selectedJob.techAssign}</p>
                <p>
  <strong>Job Total:</strong>{" "}
  {jobTotals[String(selectedJob.wo)] != null
    ? `$${jobTotals[String(selectedJob.wo)].toLocaleString()}`
    : "Not set"}
</p>

                <p><strong>Proposal PDF:</strong> {selectedJob.pdfURL}</p>
                <p><strong>Status:</strong> {selectedJob.status}</p>
                <p><strong>Created On:</strong> {selectedJob.creationDate ? new Date(selectedJob.creationDate.seconds * 1000).toLocaleString() : 'N/A'}</p>
                {selectedJob.proposalPrice && (
                  <p><strong>Proposal Price:</strong> ${selectedJob.proposalPrice}</p>
                )}
                {selectedJob.depositPrice && (
                  <p><strong>Deposit Price:</strong> ${selectedJob.depositPrice}</p>
                )}
                {selectedJob.depositPrice2 && (
                  <p><strong>Deposit Price 2:</strong> ${selectedJob.depositPrice2}</p>
                )}
                {selectedJob.changePrice && (
                  <p><strong>Change Price:</strong> ${selectedJob.changePrice}</p>
                )}
                {selectedJob.changePrice2 && (
                  <p><strong>Change Price 2:</strong> ${selectedJob.changePrice2}</p>
                )}
                {selectedJob.proposalPrice && (
                  <p><strong>Remaining Balance:</strong> ${calculateRemainingBalance(selectedJob)}</p>
                )}

                {selectedJob.fileURLs && selectedJob.fileURLs.length > 0 && (
                  <div>
                    <h3>Uploaded Files</h3>
                    <div className="uploaded-files">
                      {selectedJob.fileURLs.map((fileURL, index) => (
                        <div key={index}>
                          <a href={fileURL} target="_blank" rel="noopener noreferrer">
                            {fileURL.match(/\.(jpeg|jpg|gif|png)$/) ? (
                              <img src={fileURL} alt={`File ${index + 1}`} className="thumbnail" />
                            ) : (
                              `View File ${index + 1}`
                            )}
                          </a>
                          <p>{selectedJob.fileNames && selectedJob.fileNames[index]}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Debugging Section */}
                <pre>
                  {JSON.stringify(selectedJob.proposalPDFs, null, 2)}
                </pre>

                {/* Check if PDFs exist */}
                {selectedJob.proposalPDFs && selectedJob.proposalPDFs.length > 0 && (() => {
                  const latestPdf = selectedJob.proposalPDFs[selectedJob.proposalPDFs.length - 1]; // Get the last (latest) PDF
                  return (
                    <div>
                      <strong>Latest Proposal PDF:</strong>
                      <p>
                        <a href={latestPdf.pdfURL} target="_blank" rel="noopener noreferrer">
                          {latestPdf.pdfName}
                        </a> ({latestPdf.dateTimeString})
                      </p>
                    </div>
                  );
                })()}

                <h3>Notes</h3>
                <div className="notes-container">
                  {Array.isArray(selectedJob.notes) && selectedJob.notes.length > 0 ? (
                    <ul>
                      {selectedJob.notes.map((note, index) => (
                        <li key={index}>
                          <strong>{note.author} ({note.timestamp}):</strong> {note.text}
                          {note.files && note.files.map((file, fileIndex) => (
  <p key={fileIndex}>
    <a href={file.fileURL} target="_blank" rel="noopener noreferrer">
      {file.fileURL.match(/\.(jpeg|jpg|gif|png)$/i) ? (
        <img src={file.fileURL} alt={`Attachment ${fileIndex + 1}`} className="thumbnail" />
      ) : (
        `View Attachment ${fileIndex + 1}`
      )}
    </a>
  </p>
))}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No notes yet.</p>
                  )}
                </div>

                <div className="modal-buttons">
                  <button className='grey-button' onClick={handleEditClick}>Edit</button>
                  <button className="azul-button" onClick={() => setShowNotePopup(true)}>Add Note</button>
                  <button className="green-button" onClick={() => setShowVerificationPopup(true)}>
                    {getButtonTitle(selectedJob.serviceType)}
                  </button>
                  <button
  className="green-button"
  onClick={() => {
    setTotalInput(jobTotals[String(selectedJob.wo)] ?? "");
    setShowTotalPopup(true);
  }}
>
  {jobTotals[String(selectedJob.wo)] != null ? "Change Total" : "Add Total"}
</button>



                  <button className="red-button" onClick={closeModal}>Close</button>
                </div>
                <div className="modal-buttons">
                  <button className="orange-button" onClick={() => setShowDepositPrice2Popup(true)}>Add Deposit Price</button>
                  <button className="teal-button" onClick={() => setShowChangePricePopup(true)}>Add Change Price 1</button>
                  <button className="plum-button" onClick={() => setShowChangePrice2Popup(true)}>Add Change Price 2</button>
                </div>
              </>
            )}
          </div>
        </>
      )}

{showNotePopup && (
        <>
          <div className="modal-overlay" onClick={closeNotePopup}></div>
          <div className="modal">
            <h2>Add Note</h2>
            <textarea className="form-container" rows="4" placeholder="Enter your note here..." value={noteText} onChange={(e) => setNoteText(e.target.value)}></textarea>
            <input type="file" accept="image/*, .pdf" multiple onChange={handleFileChange} /> {/* Allow multiple file selection */}
            <div className="modal-buttons">
              <button className="green-button" onClick={saveNote} disabled={uploading}>{uploading ? "Uploading..." : "Save"}</button>
              <button className="red-button" onClick={closeNotePopup}>Cancel</button>
            </div>
          </div>
        </>
      )}

{showVerificationPopup && (
        <>
          <div className="modal-overlay" onClick={closeVerificationPopup}></div>
          <div className="modal">
            <h3>{getVerificationMessage(selectedJob.serviceType)}</h3>
            <div className="button-group">
            <button
  className="shortgreen-button"
  onClick={markJobCompleted}

>
  Yes
</button>
              <button className="shortred-button" onClick={closeVerificationPopup}>No</button>
            </div>
          </div>
        </>
      )}

{showTotalPopup && (
  <>
    <div className="modal-overlay" onClick={() => setShowTotalPopup(false)} />
    <div className="modal">
      <h2>{jobTotals[String(selectedJob.wo)] != null ? "Change Job Total" : "Add Job Total"}</h2>

      <label>
        Total Amount
        <div className="price-input">
          <span>$</span>
          <input
            type="number"
            value={totalInput}
            onChange={(e) => setTotalInput(e.target.value)}
          />
        </div>
      </label>

      <div className="modal-buttons">
        <button
          className="green-button"
          onClick={async () => {
            const jobKey = String(selectedJob.wo);

            const parsedTotal = Number(totalInput);

if (Number.isNaN(parsedTotal) || parsedTotal <= 0) {
  alert("Please enter a valid total amount.");
  return;
}

            await addJobTotal({
              jobId: jobKey,
              wo: jobKey,
              proposalId: selectedJob.proposalId || null,
              total: parsedTotal, // ✅ SAFE VALUE
              source: "admin",
            });

            // update UI immediately
            setJobTotals((prev) => ({
              ...prev,
              [jobKey]: Number(totalInput),
            }));

            setShowTotalPopup(false);
          }}
        >
          Save
        </button>

        <button
          className="red-button"
          onClick={() => setShowTotalPopup(false)}
        >
          Cancel
        </button>
      </div>
    </div>
  </>
)}

{showDepositPrice2Popup && (
      <>
        <div className="modal-overlay" onClick={() => setShowDepositPrice2Popup(false)}></div>
        <div className="modal">
          <h2>Add Deposit Price 2</h2>
          <label>
            Price:
            <div className="price-input">
              <span>$</span>
              <input
                type="number"
                value={depositPrice2}
                onChange={(e) => setDepositPrice2(e.target.value)}
              />
            </div>
          </label>
          <div className="modal-buttons">
            <button className="green-button" onClick={handleSaveDepositPrice2}>Save</button>
            <button className="red-button" onClick={() => setShowDepositPrice2Popup(false)}>Cancel</button>
          </div>
        </div>
      </>
    )}

{showChangePricePopup && (
      <>
        <div className="modal-overlay" onClick={() => setShowChangePricePopup(false)}></div>
        <div className="modal">
          <h2>Add Change Price</h2>
          <label>
            Price:
            <div className="price-input">
              <span>$</span>
              <input
                type="number"
                value={changePrice}
                onChange={(e) => setChangePrice(e.target.value)}
              />
            </div>
          </label>
          <div className="modal-buttons">
            <button className="green-button" onClick={handleSaveChangePrice}>Save</button>
            <button className="red-button" onClick={() => setShowChangePricePopup(false)}>Cancel</button>
          </div>
        </div>
      </>
    )}

{showChangePrice2Popup && (
      <>
        <div className="modal-overlay" onClick={() => setShowChangePrice2Popup(false)}></div>
        <div className="modal">
          <h2>Add Change Price 2</h2>
          <label>
            Price:
            <div className="price-input">
              <span>$</span>
              <input
                type="number"
                value={changePrice2}
                onChange={(e) => setChangePrice2(e.target.value)}
              />
            </div>
          </label>
          <div className="modal-buttons">
            <button className="green-button" onClick={handleSaveChangePrice2}>Save</button>
            <button className="red-button" onClick={() => setShowChangePrice2Popup(false)}>Cancel</button>
          </div>
        </div>
      </>
    )}

      {/** Admin Portal Button **/}
      {userRole === "Admin" && (
        <div className="admin-portal-container">
        
          {["Admin","Manager","Assistant"].includes(userRole) && (
  <div className="admin-portal-container" style={{ gap: 8 }}>
    <button className="smallgreen-button" onClick={() => navigate("/Accounting")}>
      Accounting
    </button>
    <button className="smallred-button" onClick={() => navigate("/Financials")}>
      Open Financials Dashboard
    </button>
    <button className="smallgreen-button" onClick={() => navigate("/OverheadSettings")}>
      Overhead Settings
    </button>
    <button className="smallazul-button" onClick={() => navigate("/DailyLabor")}>
      Tech Daily Pay
    </button>
    <button className="smallred-button" onClick={() => navigate("/DailySpending")}>
      Daily Spending
    </button>
    <button className="smallgreen-button" onClick={() => navigate("/BillsCredits")}>
      Bills Credits
    </button>
    <button className="smallazul-button" onClick={() => navigate("/Deposits")}>
      Deposits
    </button>
    <button className="smallgreen-button" onClick={() => navigate("/SendInvoices")}>
      Send Invoices
    </button>
    <button className="smallazul-button" onClick={() => navigate("/ApprovedInvoices")}>
      Approved Invoices
    </button>
    <button className="smallred-button" onClick={() => navigate("/StaffSettings")}>
      Staff Settings
    </button>
    <button className="smallgreen-button" onClick={() => navigate("/notification-recipients")}>
      Notifications
    </button>
  </div>
)}

        </div>
      )}
    </div>
  );
};

export default AdminPortal;
